'use client';

import { useEffect, useRef, useState } from 'react';
import { Icon } from './profile/icon';

/**
 * Formats a US phone for display: 10 digits → "(239) 555-1000", 11 digits
 * (leading 1) → "+1 (239) 555-1000". Anything else falls back to the raw input.
 * Shared by `AgentCard` (search grid) and the profile hero / contact rail.
 */
export function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    const d = digits.slice(1);
    return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return raw;
}

export interface PhoneRevealProps {
  /** The agent's phone (raw). NEVER rendered into the DOM until revealed. */
  phone: string;
  /** Fired EXACTLY once, the first time the number is revealed. */
  onReveal: () => void;
  /** Control scale — 'sm' for the search card, 'md' for the profile surfaces. */
  size?: 'sm' | 'md';
  /** Override the pre-reveal button label (default "Show phone number"). */
  label?: string;
  /**
   * Visual treatment — 'link' (default; the inline text control every existing
   * surface uses) or 'button' (a full-width primary-filled CTA, the property
   * page's Listing Agent card). Same reveal invariant either way.
   */
  variant?: 'link' | 'button';
}

/**
 * Phone-reveal control — the Phase 31 privacy invariant, extracted here so the
 * search card (`surface='search_card'`), the profile hero (`'profile_hero'`),
 * and the contact rail (`'contact_rail'`) share ONE implementation.
 *
 * KEY INVARIANT: the phone string is never in the DOM until the visitor clicks
 * "Show phone number" — it is conditionally rendered (not CSS-hidden), and
 * `onReveal` fires exactly once on first reveal. The parent wires `onReveal` to
 * the fire-and-forget interaction POST (carrying its surface); the reveal is
 * optimistic, so a failed POST never blocks the number from showing.
 */
export function PhoneReveal({
  phone,
  onReveal,
  size = 'sm',
  label = 'Show phone number',
  variant = 'link',
}: PhoneRevealProps) {
  const [revealed, setRevealed] = useState(false);
  const link = useRef<HTMLAnchorElement>(null);
  useEffect(() => { if (revealed) link.current?.focus(); }, [revealed]);
  const fontSize = size === 'md' ? '15px' : '14px';
  const iconSize = size === 'md' ? 18 : 16;

  const isButton = variant === 'button';
  const className = isButton
    ? 'inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 font-semibold'
    : 'inline-flex items-center gap-1.5 font-medium';
  const style: React.CSSProperties = isButton
    ? {
        backgroundColor: 'var(--brand-button-bg, var(--brand-primary))',
        color: 'var(--brand-primary-foreground)',
        borderRadius: 'var(--radius-button, 6px)',
        fontSize,
      }
    : { color: 'var(--brand-primary-text, var(--brand-primary))', fontSize };

  if (revealed) {
    return (
      <a
        ref={link}
        href={`tel:${phone.replace(/\D/g, '')}`}
        className={className}
        style={style}
      >
        <Icon name="phone_enabled" size={iconSize} />
        {formatPhone(phone)}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        onReveal();
        setRevealed(true);
      }}
      className={className}
      style={style}
    >
      <Icon name="phone_enabled" size={iconSize} />
      {label}
    </button>
  );
}
